import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  UserCog, LogOut, Save, Loader2, Camera, X, Car, IdCard, Globe,
  Trash2, CheckCircle2, ShieldAlert, ArrowLeft,
} from "lucide-react";
import {
  talentApi, isTalentAuthenticated, talentLogout,
} from "../../lib/talentPortalApi";
import { api, formatApiError, fileUrl } from "../../lib/api";
import { useConfirm } from "../../hooks/useConfirm";

const LANGS = ["Português", "Inglês", "Espanhol", "Crioulo", "Francês"];
const AVAIL = ["Integral", "Meio período", "Fins de semana", "Noites", "Flexível"];
const WEEKDAYS = [
  { id: 0, short: "Dom" }, { id: 1, short: "Seg" }, { id: 2, short: "Ter" },
  { id: 3, short: "Qua" }, { id: 4, short: "Qui" }, { id: 5, short: "Sex" },
  { id: 6, short: "Sáb" },
];

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
    {children}
  </div>
);

export default function TalentPortalDashboard() {
  const nav = useNavigate();
  const confirm = useConfirm();
  const [f, setF] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!isTalentAuthenticated()) {
      nav("/portal-colaborador", { replace: true });
      return;
    }
    (async () => {
      try {
        const { data } = await talentApi.me();
        setF({
          full_name: data.full_name || "", phone: data.phone || "",
          address: data.address || "", city: data.city || "", state: data.state || "",
          zip_code: data.zip_code || "", has_license: !!data.has_license,
          has_car: !!data.has_car, vehicle_type: data.vehicle_type || "",
          languages: data.languages || [], work_days: data.work_days || [],
          availability: data.availability || "", experience: data.experience || "",
          photo_path: data.photo_path || "",
          travel_radius_miles: data.travel_radius_miles || 30,
          email: data.email || "",
          portal_cancelled: !!data.portal_cancelled,
        });
        if (data.portal_cancelled) setCancelled(true);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, [nav]);

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Envie uma imagem");
    if (file.size > 10 * 1024 * 1024) return toast.error("Imagem muito grande (máx. 10MB)");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/public/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("photo_path", data.path);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const toggleLang = (l) =>
    setF((p) => ({
      ...p,
      languages: p.languages.includes(l)
        ? p.languages.filter((x) => x !== l)
        : [...p.languages, l],
    }));

  const save = async (e) => {
    e.preventDefault();
    if (!f.full_name.trim()) return toast.error("Informe seu nome completo");
    setSaving(true);
    try {
      const payload = {
        full_name: f.full_name, phone: f.phone, address: f.address, city: f.city,
        state: f.state, zip_code: f.zip_code, has_license: f.has_license,
        has_car: f.has_car, vehicle_type: f.vehicle_type, languages: f.languages,
        work_days: f.work_days, availability: f.availability, experience: f.experience,
        travel_radius_miles: f.travel_radius_miles, photo_path: f.photo_path,
      };
      await talentApi.update(payload);
      toast.success("Dados atualizados!");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const logout = () => {
    talentLogout();
    nav("/portal-colaborador", { replace: true });
  };

  const doCancel = async () => {
    const ok = await confirm({
      title: "Cancelar minha inscrição?",
      description:
        "Seu cadastro ficará inativo e você deixará de aparecer para as empresas. Você pode se cadastrar novamente no futuro.",
      destructive: true,
      actionLabel: "Sim, cancelar inscrição",
    });
    if (!ok) return;
    try {
      await talentApi.cancel();
      setCancelled(true);
      toast.success("Inscrição cancelada.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        <Loader2 className="animate-spin mr-2" /> Carregando…
      </div>
    );
  }

  if (cancelled) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border p-8 text-center" data-testid="tp-cancelled-card">
          <ShieldAlert className="mx-auto text-amber-500" size={52} />
          <h2 className="text-2xl font-bold mt-4">Inscrição cancelada</h2>
          <p className="text-slate-600 mt-2">
            Seu cadastro está inativo. Se mudar de ideia, você pode se cadastrar novamente.
          </p>
          <div className="flex flex-col gap-2 mt-6">
            <a href="/seja-colaborador" className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3" data-testid="tp-recadastrar">
              Cadastrar novamente
            </a>
            <button onClick={logout} className="w-full rounded-xl border py-3 text-slate-700 hover:bg-slate-50 font-medium" data-testid="tp-cancelled-logout">
              Sair
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center">
              <UserCog size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Meus dados</h1>
              <p className="text-sm text-slate-500">{f.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              data-testid="tp-back-to-site"
              className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-emerald-700 font-medium"
            >
              <ArrowLeft size={16} /> Voltar ao site
            </a>
            <button
              onClick={logout}
              data-testid="tp-logout"
              className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 font-medium"
            >
              <LogOut size={16} /> Sair
            </button>
          </div>
        </div>

        <form onSubmit={save} className="bg-white rounded-2xl shadow-sm border p-6 space-y-5" data-testid="tp-profile-form">
          <div className="flex flex-col items-center gap-3">
            <div className="relative">
              {f.photo_path ? (
                <img
                  src={fileUrl(f.photo_path)}
                  alt="Sua foto"
                  className="w-24 h-24 rounded-full object-cover border-2 border-emerald-100"
                  data-testid="tp-photo-preview"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                  <Camera size={28} />
                </div>
              )}
              {f.photo_path && (
                <button
                  type="button"
                  onClick={() => set("photo_path", "")}
                  data-testid="tp-photo-remove"
                  className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-white border shadow flex items-center justify-center text-rose-600"
                  aria-label="Remover foto"
                >
                  <X size={15} />
                </button>
              )}
            </div>
            <label className="inline-flex items-center gap-2 px-4 min-h-[40px] rounded-lg border bg-white text-slate-700 hover:bg-slate-50 text-sm font-medium cursor-pointer" data-testid="tp-photo-btn">
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
              {uploading ? "Enviando…" : f.photo_path ? "Trocar foto" : "Adicionar foto"}
              <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="tp-photo-input" disabled={uploading} />
            </label>
          </div>

          <Field label="Nome completo *">
            <input className="cm-input w-full" data-testid="tp-full-name" value={f.full_name} onChange={(e) => set("full_name", e.target.value)} />
          </Field>
          <Field label="Telefone">
            <input className="cm-input w-full" data-testid="tp-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} />
          </Field>
          <Field label="Endereço">
            <input className="cm-input w-full" data-testid="tp-address" value={f.address} onChange={(e) => set("address", e.target.value)} />
          </Field>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Cidade"><input className="cm-input w-full" data-testid="tp-city" value={f.city} onChange={(e) => set("city", e.target.value)} /></Field>
            <Field label="Estado"><input className="cm-input w-full" data-testid="tp-state" value={f.state} onChange={(e) => set("state", e.target.value)} /></Field>
            <Field label="CEP"><input className="cm-input w-full" data-testid="tp-zip" value={f.zip_code} onChange={(e) => set("zip_code", e.target.value)} /></Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
              <input type="checkbox" className="w-5 h-5 accent-emerald-600" data-testid="tp-has-license" checked={f.has_license} onChange={(e) => set("has_license", e.target.checked)} />
              <IdCard size={18} className="text-slate-500" /> Possui Driver (habilitação)
            </label>
            <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
              <input type="checkbox" className="w-5 h-5 accent-emerald-600" data-testid="tp-has-car" checked={f.has_car} onChange={(e) => set("has_car", e.target.checked)} />
              <Car size={18} className="text-slate-500" /> Possui carro próprio
            </label>
          </div>
          {f.has_car && (
            <Field label="Tipo de veículo">
              <input className="cm-input w-full" data-testid="tp-vehicle" value={f.vehicle_type} onChange={(e) => set("vehicle_type", e.target.value)} placeholder="Ex.: Sedan, SUV, Van" />
            </Field>
          )}

          <Field label={<span className="flex items-center gap-1.5"><Globe size={15} /> Idiomas</span>}>
            <div className="flex flex-wrap gap-2">
              {LANGS.map((l) => (
                <button type="button" key={l} data-testid={`tp-lang-${l}`} onClick={() => toggleLang(l)}
                  className={`px-3 py-2 rounded-full text-sm border transition-colors ${f.languages.includes(l) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                  {l}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Disponibilidade">
            <select className="cm-input w-full" data-testid="tp-availability" value={f.availability} onChange={(e) => set("availability", e.target.value)}>
              <option value="">Selecione…</option>
              {AVAIL.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </Field>

          <Field label="Raio de alcance — até onde você está disposto a ir?">
            <div className="flex items-center gap-3">
              <input
                type="range" min="5" max="100" step="5"
                data-testid="tp-radius-range"
                value={f.travel_radius_miles || 30}
                onChange={(e) => set("travel_radius_miles", Number(e.target.value))}
                className="flex-1 accent-emerald-600"
              />
              <span className="w-24 text-center text-sm font-semibold text-emerald-700 bg-emerald-50 rounded-lg py-1.5" data-testid="tp-radius-value">
                {f.travel_radius_miles || 30} mi
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-1.5">Você só aparecerá para clientes dentro dessa distância.</p>
          </Field>

          <Field label="Dias de trabalho na semana">
            <div className="flex flex-wrap gap-2" data-testid="tp-workdays">
              {WEEKDAYS.map((w) => {
                const active = f.work_days.includes(w.id);
                return (
                  <button
                    type="button" key={w.id} data-testid={`tp-wd-${w.id}`}
                    onClick={() =>
                      setF((p) => ({
                        ...p,
                        work_days: p.work_days.includes(w.id)
                          ? p.work_days.filter((x) => x !== w.id)
                          : [...p.work_days, w.id].sort((a, b) => a - b),
                      }))
                    }
                    className={`w-14 h-12 rounded-xl text-sm font-semibold border transition-colors ${
                      active ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                             : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"
                    }`}
                  >
                    {w.short}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label="Experiência">
            <textarea className="cm-input w-full min-h-[90px]" data-testid="tp-experience" value={f.experience} onChange={(e) => set("experience", e.target.value)} placeholder="Conte sua experiência em limpeza / serviços" />
          </Field>

          <button type="submit" disabled={saving} data-testid="tp-save"
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl py-3 flex items-center justify-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
            Salvar alterações
          </button>
        </form>

        <div className="mt-6 bg-white rounded-2xl border border-rose-100 p-5">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center shrink-0">
              <Trash2 size={18} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900">Cancelar inscrição</h3>
              <p className="text-sm text-slate-500 mt-0.5">
                Seu cadastro ficará inativo e você deixará de aparecer para as empresas.
              </p>
              <button
                onClick={doCancel}
                data-testid="tp-cancel-registration"
                className="mt-3 inline-flex items-center gap-2 rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50 px-4 py-2 text-sm font-medium"
              >
                <Trash2 size={15} /> Cancelar minha inscrição
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
